import { setIsPollingRequest } from "./pollingContext";

type EndpointType = "waiver" | "start" | "rundown" | "DraftAdvice" | "analyze_trade" | "common" | "super";

interface StreamPollingConfig {
  endpointType: EndpointType;
  leagueId: string;
  teamName?: string; // Make teamName optional for trade analysis
  yahooToken?: string;
  signal?: AbortSignal;
  // NEW: Add the context to the config
  context: { platformId: string; leagueId: string; userId: string | undefined };
  // API & Auth
  getAccessTokenSilently: () => Promise<string | null>;
  authId: string;
  currentUserDataPlatform: any;
  pocToken: string;

  // API Call Function
  apiCall: (
    authId: string,
    userDataPlatform: any,
    leagueId: string,
    teamName: string,
    pocToken: string,
    accessToken: string,
    signal?: AbortSignal,
    yahooToken?: string,
    // NEW: Add extra arguments
    apiCallArgs?: any,
  ) => Promise<any>;

  // State Setters
  setStreamState: (updater: (prev: any) => any) => void;
  setData: (data: any) => void;
  setLoading: (loading: boolean) => void;
  updateSectionData: (section: string, data: any) => void;
  setEndpointErrors: (updater: (prev: any) => any) => void;
  setErrorMessages: (updater: (prev: any) => any) => void;

  // NEW: Add a way to tell the UI the data is from cache
  setHasAnimationCompleted: (completed: boolean) => void;

  // NEW: Pass the lock management down to the polling function
  requestKey: string;
  activeStreamRequests: React.MutableRefObject<Set<string>>;

  // Messages
  retrievingMessage: string;
  errorKey: string; // "Waiver", "Start", "Rundown" for the error message (NOT ERROR KEY ENDPOINT FOR RETRY)

  // NEW: Add a ref to the currently selected team info
  currentTeamInfoRef: React.RefObject<{ platformId: string; leagueId: string; teamName: string } | null>;
  // NEW: Add a flexible way to pass extra arguments to the API call
  apiCallArgs?: any;

  // NEW: Optional override for section name used in cache updates (e.g., "commonLeagueData")
  sectionKey?: string;

  // NEW: Optional callback invoked with the final assembled data
  onComplete?: (finalData: any) => void;
  /** Cached SSE includes audio_summary; live handlers set this, polling must too. */
  onAudioUrl?: (url: string) => void;
  setThoughtsCommon?: (thoughts: string) => void;

  // NEW: Optional flag to avoid toggling loading when UI already has content
  suppressLoading?: boolean;

  // NEW: Optional flag to completely suppress polling (used during refresh for same team)
  suppressPolling?: boolean;
}

export const startPollingForCompletion = async (config: StreamPollingConfig): Promise<void> => {
  const pollInterval = 2000; // each 2 seconds we check
  const timeout = 180000; // 180 seconds = 3 minutes in milliseconds
  // const timeout = 150000; // 150 seconds = 2.5 minutes in milliseconds

  // Record the start time of the polling process.
  const startTime = Date.now();

  // Helper function to check if the current team context is still valid
  const isContextValid = () => {
    const currentTeam = config.currentTeamInfoRef.current;
    const pollingContext = config.context;
    return currentTeam && currentTeam.platformId === pollingContext.platformId && currentTeam.leagueId === pollingContext.leagueId && currentTeam.teamName === config.teamName;
  };

  // Early exit: if caller requests to suppress polling for this valid context (e.g., manual refresh)
  if (config.suppressPolling && isContextValid()) {
    console.log(`[Polling] Suppressed for ${config.endpointType} due to refresh context (same team).`);
    return;
  }

  // Reaffirm that we are in a loading state, unless caller wants to avoid flicker.
  // CRITICAL: Only update if context is still valid
  if (!config.suppressLoading && isContextValid()) {
    // if ( isContextValid()) {
    // This is crucial for when the polling takes control of an aborted request.
    config.setLoading(true);
  }

  // Set initial polling state (skip if we want to avoid flicker)
  // CRITICAL: Only update if context is still valid
  if (!config.suppressLoading && isContextValid()) {
    // CRITICAL: Set teamKey so AnimatedButton can detect loading state during polling
    const teamKey = `${config.context.platformId}-${config.context.leagueId}`;
    config.setStreamState((prev) => ({
      ...prev,
      status: config.retrievingMessage,
      teamKey: teamKey,
      // isStreamActive: true,
    }));
  }

  let isFirstPoll = true;

  // The loop now checks two conditions: if the request has been aborted,
  // OR if the elapsed time has exceeded our timeout.
  while (!config.signal?.aborted && Date.now() - startTime < timeout) {
    try {
      // Only wait if it's NOT the first poll attempt
      if (!isFirstPoll) {
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
        // Check again after waiting in case of cancellation
        if (config.signal?.aborted) return;
        // CRITICAL: Check if the request key was removed (e.g., during refresh)
        if (!config.activeStreamRequests.current.has(config.requestKey)) {
          console.log(`${config.endpointType}: Request key removed from activeStreamRequests (likely due to refresh). Stopping poll.`);
          // Reset loading state so button becomes clickable again
          if (isContextValid()) {
            config.setLoading(false);
            config.setStreamState((prev) => ({ ...prev, isStreamActive: false, isGeneratingResult: false }));
          }
          return;
        }
        // CRITICAL: Check if context is still valid after waiting
        if (!isContextValid()) {
          console.log(`${config.endpointType}: Context changed during polling. Stopping poll.`);
          return;
        }
      }
      isFirstPoll = false;

      // CRITICAL: Check if the request key was removed before each poll attempt
      if (!config.activeStreamRequests.current.has(config.requestKey)) {
        console.log(`${config.endpointType}: Request key removed from activeStreamRequests (likely due to refresh). Stopping poll.`);
        // Reset loading state so button becomes clickable again
        if (isContextValid()) {
          config.setLoading(false);
          config.setStreamState((prev) => ({ ...prev, isStreamActive: false, isGeneratingResult: false }));
        }
        return;
      }

      // Get the token using the (potentially safe) function passed in config.
      const accessTokenFromAuth0 = await config.getAccessTokenSilently();

      //new guard clause
      if (!accessTokenFromAuth0) {
        // If the token is null, it means logout has been triggered by the safe hook.
        // We must stop the polling loop immediately.
        console.warn(`${config.endpointType}: Could not get access token. Logout is in progress. Stopping poll.`);
        // Ensure UI is not stuck in a loading state.
        // CRITICAL: Only update if context is still valid
        if (isContextValid()) {
          config.setLoading(false);
          config.setStreamState((prev) => ({ ...prev, isStreamActive: false, isGeneratingResult: false }));
        }
        return; // EXIT THE POLLING LOOP
      }

      // CRITICAL: Check context before making API call
      if (!isContextValid()) {
        console.log(`${config.endpointType}: Context changed during polling. Stopping poll.`);
        return;
      }

      // If we get here, the token is valid. The rest of the logic can proceed.
      // Mark the upcoming fetch as a polling request so getXStream functions
      // add the X-Polling-Request header (bypasses rate-limit guard).
      // Safe: JS is single-threaded, flag is read synchronously before the
      // first `await` inside apiCall, then cleared immediately.
      setIsPollingRequest(true);
      let apiCallPromise: Promise<any>;
      try {
        apiCallPromise = config.apiCall(
          config.authId,
          config.currentUserDataPlatform,
          config.leagueId,
          config.teamName,
          config.pocToken,
          accessTokenFromAuth0,
          config?.signal,
          config.yahooToken,
          // NEW: Pass extra arguments
          config.apiCallArgs,
        );
      } finally {
        setIsPollingRequest(false);
      }
      const response = await apiCallPromise;

      if (response === "INCOMPLETE") {
        console.log(`${config.endpointType}: Still INCOMPLETE, continuing polling...`);
        continue;
      }

      // CRITICAL: Check context again before processing response
      if (!isContextValid()) {
        console.log(`${config.endpointType}: Context changed while processing response. Stopping poll.`);
        return;
      }

      // If we reach here, the response is NOT "INCOMPLETE". It must be a stream.
      if (response && response.body) {
        console.log(`${config.endpointType}: Polling successful. Received stream from cache.`);

        // KEY CHANGE: Since this is from cache, we can immediately tell the UI to skip the animation
        // CRITICAL: Only update if context is still valid
        if (isContextValid()) {
          config.setHasAnimationCompleted(true);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        // We need to build the final object to update the state at the end
        let finalData: any = null;
        let fullAnalysis = "";
        const charts = [];
        // NEW: Add specific fields for trade analysis
        const giveSummary: any[] = [];
        const receiveSummary: any[] = [];

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            break; // Exit the read loop
          }

          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split("\n\n");
          buffer = parts.pop() || "";

          for (const part of parts) {
            if (!part.startsWith("data: ")) continue;
            try {
              const jsonData = part.substring(6);
              const event = JSON.parse(jsonData);

              switch (event.type) {
                case "data":
                  if (config.endpointType === "common" || config.endpointType === "super") {
                    finalData = event.payload;
                  }
                  break;
                case "text_chunk":
                  fullAnalysis += event.payload;
                  break;
                case "chart_data":
                  // Add placeholder for correct ordering
                  // fullAnalysis += `[chart_${charts.length}]`;
                  charts.push(event.payload);
                  break;
                // NEW: Handle trade-specific events
                case "give_summary":
                  giveSummary.push(event.payload);
                  break;
                case "receive_summary":
                  receiveSummary.push(event.payload);
                  break;
                case "audio_summary":
                  if (typeof event.payload === "string" && event.payload) {
                    finalData = finalData || {};
                    finalData.audioUrl = event.payload;
                  }
                  break;
                case "complete":
                  finalData = finalData || {};
                  finalData.request_id = event.payload.request_id;
                  break;
              }
              if (event.payload?.messageId) {
                // Align with TradeResponseStream which uses messageId for thumbs/share
                finalData.messageId = event.payload.messageId;
              }
            } catch (e) {
              console.error("Failed to parse JSON from polled stream:", e, part);
            }
          }
        }

        // Now that the stream is fully read, construct the final object
        if (config.endpointType === "common" || config.endpointType === "super") {
          // finalData already assembled from 'data' events
          finalData = finalData || {};
        } else {
          const analysisKey = config.endpointType === "analyze_trade" ? "trade_analysis" : `${config.endpointType}_analysis`;
          finalData = finalData || {};
          // DraftAdvice reuses rundown UI shape: rundown_analysis + chart_data
          if (config.endpointType === "DraftAdvice") {
            finalData.rundown_analysis = fullAnalysis;
          } else {
            finalData[analysisKey] = fullAnalysis;
          }
          finalData.chart_data = charts;
          if (config.endpointType === "analyze_trade") {
            finalData.give_summary = giveSummary;
            finalData.receive_summary = receiveSummary;
          }
        }
        // CRITICAL FIX: Attach the context that was passed in.
        finalData.context = config.context;

        // BATCH UPDATE STATE aFTER STREAM IS FULLY PROCESSED
        // CRITICAL: Only update UI state if the user is still viewing the relevant team.
        // UPDATED: Now includes teamName check
        if (isContextValid()) {
          console.log(`${config.endpointType}: Stream processing complete. Updating UI state.`);
          config.setData(finalData);
          if (finalData?.audioUrl && config.onAudioUrl) {
            config.onAudioUrl(finalData.audioUrl);
          }
          config.setLoading(false);
          // CRITICAL: Preserve teamKey when updating streamState after polling completes
          const teamKey = `${config.context.platformId}-${config.context.leagueId}`;
          config.setThoughtsCommon?.("");
          config.setStreamState((prev) => ({ ...prev, isGeneratingResult: true, isStreamActive: false, teamKey: teamKey }));
          config.setEndpointErrors((prev) => ({ ...prev, [config.endpointType]: false }));
        } else {
          console.log(`${config.endpointType}: Stream processing complete, but user has navigated away. Caching data in background.`);
        }

        // ALWAYS update the central cache, regardless of what the user is viewing.
        const section = config.sectionKey ?? config.endpointType;
        config.updateSectionData(section, finalData);

        // Optional callback for callers needing final data
        if (config.onComplete) {
          config.onComplete(finalData);
        }

        return; // EXIT THE POLLING LOOP
      }

      // If for some reason the response is neither "INCOMPLETE" nor a valid stream,
      // we log it and continue polling to be safe.
      console.warn(`Polling for ${config.endpointType} received an unexpected response:`, response);
    } catch (error) {
      if (error.name === "AbortError") {
        // If the user explicitly cancels the request, stop everything.
        return;
      }

      // Rate limit (429): show friendly message, never fall back to polling
      if ((error as any).isRateLimit) {
        if (isContextValid()) {
          config.setLoading(false);
          config.setStreamState((prev) => ({ ...prev, isStreamActive: false, isGeneratingResult: false }));
          config.setErrorMessages((prev) => ({
            ...prev,
            [config.errorKey]: "You're making too many requests. Please slow down and try again in a moment.",
          }));
        }
        return;
      }

      // NEW: Explicitly check for the "FAILED" state from the backend.
      // This is a terminal state that should stop the polling loop.
      if (error.message && (error.message.includes("FAILED") || error.message.includes("There was an error with RotoGPT processing"))) {
        console.error(`${config.endpointType}: Polling received FAILED status from backend. Stopping poll.`);

        // CRITICAL: Only show error if the user is still viewing the relevant team.
        // UPDATED: Now uses isContextValid() helper
        if (isContextValid()) {
          config.setLoading(false);
          config.setStreamState((prev) => ({ ...prev, isStreamActive: false, isGeneratingResult: false }));
          config.setEndpointErrors((prev) => ({ ...prev, [config.endpointType]: true }));
          config.setErrorMessages((prev) => ({
            ...prev,
            [config.errorKey]: "There was an error with RotoGPT processing of your league data.",
          }));
        } else {
          console.log(`Polling for ${config.context.leagueId} received FAILED status, but user is on a different team. Suppressing UI error.`);
        }
        return; // EXIT THE POLLING LOOP
      }

      // For any other error (like a transient network failure),
      // log it for debugging but continue the polling loop.
      console.warn(`Polling for ${config.endpointType} encountered an error: "${error.message}". Retrying in ${pollInterval}ms...`);
      continue;
    }
  }

  // After the loop, we check if we exited because of the timeout.
  if (!config.signal?.aborted && Date.now() - startTime >= timeout) {
    console.error(`${config.endpointType}: Polling timed out after ${timeout / 1000} seconds. Stopping poll.`);
    config.setThoughtsCommon?.("");
    // THE CRUCIAL FIX: Release the lock so the user can retry.
    config.activeStreamRequests.current.delete(config.requestKey);

    // CRITICAL: Only show error if the user is still viewing the relevant team.
    // UPDATED: Now uses isContextValid() helper
    if (isContextValid()) {
      config.setLoading(false);
      config.setStreamState((prev) => ({ ...prev, isStreamActive: false, isGeneratingResult: false }));
      const errorKey = `${config.endpointType}-${config.context.platformId}-${config.context.leagueId}`;
      config.setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
      config.setErrorMessages((prev) => ({
        ...prev,
        [config.errorKey]: "There was an error with RotoGPT processing of your league data",
      }));
    } else {
      console.log(`Polling for ${config.context.leagueId} timed out, but user is on a different team. Suppressing UI error.`);
    }
  }
};
